import { Email, EmailContent, EmailThread, EmailAttachment } from '@/types/email';
import type { SenderUnsubscribeOutcome, SenderUnsubscribeTier, UnsubscribeMethod } from '@/types/email-list.types';
import { fetchWithCSRF } from './csrf';
import { EmailActionsClient } from '@/lib/cloudflare/email-actions';
import { getStoredSessionId } from '@/lib/cloudflare/auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

function getBearerAuthHeaders(accessToken: string): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

class EmailError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'EmailError';
    this.status = status;
    this.code = code;
  }
}

async function handleResponse(response: Response) {
  const data = await response.json().catch(() => ({
    message: response.statusText || 'Failed to parse response',
    code: 'UNKNOWN_ERROR'
  }));

  if (!response.ok) {
    const error = new EmailError(
      data.message || 'Request failed',
      response.status,
      data.code
    );
    // Ensure the error has a status property
    error.status = response.status;
    throw error;
  }
  return data;
}

// Cached CF client instance
let cfClient: EmailActionsClient | null = null;

/**
 * Get or create CF client for email actions
 * Returns null if CF Worker is not available
 */
export function getCfClient(accessToken: string): EmailActionsClient | null {
  if (!accessToken) return null;

  // Create new client if token changed or doesn't exist
  if (!cfClient) {
    cfClient = new EmailActionsClient(accessToken);
  }

  // Check if CF Worker is available
  if (!cfClient.isCloudflareAvailable()) {
    return null;
  }

  // Create new client with current token
  cfClient = new EmailActionsClient(accessToken);
  return cfClient;
}

export async function getEmail(emailId: string): Promise<EmailContent> {
  const response = await fetchWithCSRF(`/api/emails/${emailId}/content`);
  return handleResponse(response);
}

export async function getEmailContent(emailId: string): Promise<EmailContent> {
  const response = await fetchWithCSRF(`/api/emails/${emailId}/content`);
  return handleResponse(response);
}

export async function getEmails(): Promise<Email[]> {
  const response = await fetchWithCSRF('/api/emails');
  return handleResponse(response);
}

/**
 * Fetch emails directly from CF Worker with LLM analysis
 * This is the primary method that should be used by the frontend
 */
export async function getEmailsFromCFWorker(
  accessToken: string,
  options: {
    pageToken?: string;
    batchSize?: number;
    skipAnalysis?: boolean;
  } = {}
): Promise<{
  emails: Email[];
  nextPageToken: string | null;
}> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const sessionId = getStoredSessionId();
  const response = await fetch(`${CF_WORKER_URL}/api/gmail/list`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
    },
    body: JSON.stringify({
      accessToken,
      pageToken: options.pageToken,
      batchSize: options.batchSize || 10,
      skipAnalysis: options.skipAnalysis || false,
    }),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to fetch emails',
      response.status,
      data.error?.code
    );
  }

  return {
    emails: data.data.emails,
    nextPageToken: data.data.nextPageToken,
  };
}

/**
 * Get email content directly from CF Worker
 */
export async function getEmailContentFromCFWorker(
  emailId: string,
  accessToken: string
): Promise<EmailContent> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const response = await fetch(
    `${CF_WORKER_URL}/api/email-actions/${emailId}/content`,
    {
      method: 'GET',
      headers: getBearerAuthHeaders(accessToken),
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to fetch email content',
      response.status,
      data.error?.code
    );
  }

  // Transform CF Worker response to EmailContent format
  // API returns: { htmlBody, textBody, headers, snippet, attachments }
  // Frontend expects: { content, mimeType, headers, attachments }
  const apiData = data.data;
  return {
    content: apiData.htmlBody || apiData.textBody || apiData.snippet || '',
    mimeType: apiData.htmlBody ? 'text/html' : 'text/plain',
    headers: apiData.headers || {},
    attachments: apiData.attachments || [],
  };
}

/**
 * Get email thread from CF Worker
 */
export async function getEmailThreadFromCFWorker(
  threadId: string,
  accessToken: string
): Promise<EmailThread> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const response = await fetch(
    `${CF_WORKER_URL}/api/email-actions/thread/${threadId}`,
    {
      method: 'GET',
      headers: getBearerAuthHeaders(accessToken),
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to fetch thread',
      response.status,
      data.error?.code
    );
  }

  return data.data;
}

/**
 * Fetch attachment blob from CF Worker
 */
export async function fetchAttachmentBlobFromCFWorker(
  emailId: string,
  attachmentId: string,
  accessToken: string,
  mimeType?: string
): Promise<Blob> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const response = await fetch(
    `${CF_WORKER_URL}/api/email-actions/${emailId}/attachments/${attachmentId}`,
    {
      method: 'GET',
      headers: getBearerAuthHeaders(accessToken),
    }
  );

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to download attachment',
      response.status,
      data.error?.code
    );
  }

  // Convert base64url to base64
  const base64 = data.data.data.replace(/-/g, '+').replace(/_/g, '/');

  // Create blob
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
}

/**
 * Download attachment from CF Worker
 */
export async function downloadAttachmentFromCFWorker(
  emailId: string,
  attachmentId: string,
  filename: string,
  accessToken: string,
  mimeType?: string
): Promise<void> {
  const blob = await fetchAttachmentBlobFromCFWorker(emailId, attachmentId, accessToken, mimeType);

  // Create download link
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open attachment in a new tab using a blob URL
 */
export async function openAttachmentFromCFWorker(
  emailId: string,
  attachmentId: string,
  accessToken: string,
  mimeType?: string
): Promise<void> {
  const previewWindow = window.open('', '_blank');

  if (!previewWindow) {
    throw new EmailError('Popup blocked while opening attachment preview');
  }

  previewWindow.opener = null;
  previewWindow.document.title = 'Opening attachment preview...';
  previewWindow.document.body.innerHTML = `
    <div style="display:flex;min-height:100vh;align-items:center;justify-content:center;margin:0;font-family:system-ui,sans-serif;color:#4b5563;background:#f9fafb;">
      Loading attachment preview...
    </div>
  `;

  try {
    const blob = await fetchAttachmentBlobFromCFWorker(emailId, attachmentId, accessToken, mimeType);
    const url = URL.createObjectURL(blob);
    previewWindow.location.href = url;

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60_000);
  } catch (error) {
    previewWindow.close();
    throw error;
  }
}

/**
 * Check subscription status from CF Worker
 */
export async function checkSubscriptionFromCFWorker(
  userId: string,
  authSecret: string
): Promise<{
  isActive: boolean;
  subscription: {
    status: string;
    plan: string | null;
    trialEndsAt: number | null;
  } | null;
}> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const response = await fetch(`${CF_WORKER_URL}/api/subscription`, {
    headers: {
      'Authorization': `Bearer ${authSecret}`,
      'X-User-Id': userId,
    },
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to check subscription',
      response.status,
      data.error?.code
    );
  }

  return data.data;
}

export async function performEmailAction(emailId: string, action: 'mark_read' | 'mark_unread'): Promise<void> {
  try {
    const response = await fetchWithCSRF(`/api/emails/${emailId}/action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action }),
    });
    
    await handleResponse(response);
  } catch (error) {
    console.error('Error performing email action:', error);
    if (error instanceof EmailError) {
      throw error;
    }
    throw new EmailError(
      error instanceof Error ? error.message : 'Failed to perform email action',
      500,
      'REQUEST_FAILED'
    );
  }
}

export async function deleteEmail(emailId: string): Promise<void> {
  const response = await fetchWithCSRF(`/api/emails/${emailId}`, {
    method: 'DELETE',
  });

  await handleResponse(response);
}

/**
 * Bulk action on emails using CF Worker
 */
export async function bulkEmailAction(
  action: 'delete' | 'archive' | 'unsubscribe',
  emailIds: string[],
  accessToken?: string
): Promise<{
  results: Array<{
    status: 'success' | 'error';
    emailId: string;
    action?: string;
    error?: string;
    value?: any;
  }>;
  summary: { successCount: number; errorCount: number };
}> {
  if (!accessToken) {
    throw new EmailError('Access token required for bulk action', 401, 'AUTH_REQUIRED');
  }

  const client = getCfClient(accessToken);
  if (!client) {
    throw new EmailError('CF Worker not available', 500, 'CF_UNAVAILABLE');
  }

  return await client.bulkAction(action, emailIds);
}

/**
 * Single email action using CF Worker
 */
export async function singleEmailAction(
  emailId: string,
  action: 'mark_read' | 'mark_unread' | 'delete' | 'archive' | 'spam',
  accessToken?: string
): Promise<void> {
  if (!accessToken) {
    throw new EmailError('Access token required for email action', 401, 'AUTH_REQUIRED');
  }

  const client = getCfClient(accessToken);
  if (!client) {
    throw new EmailError('CF Worker not available', 500, 'CF_UNAVAILABLE');
  }

  await client.singleAction(emailId, action);
}

/**
 * Handle unsubscribe using CF Worker
 */
export async function handleUnsubscribe(
  params: {
    emailId: string;
    method: Extract<UnsubscribeMethod, 'none' | 'link' | 'post' | 'list-unsubscribe'>;
    unsubscribeLink?: string;
    formData?: Record<string, string>;
    listUnsubscribeHeader?: string;
    listUnsubscribePost?: string;
    userEmail: string;
  },
  accessToken?: string
): Promise<{
  success: boolean;
  message: string;
  emailId: string;
  senderPreferenceId?: string | null;
  suppressionApplied: boolean;
  senderUnsubscribeOutcome: SenderUnsubscribeOutcome;
  senderUnsubscribeTier: SenderUnsubscribeTier;
}> {
  if (!accessToken) {
    throw new EmailError('Access token required for unsubscribe', 401, 'AUTH_REQUIRED');
  }

  const client = getCfClient(accessToken);
  if (!client) {
    throw new EmailError('CF Worker not available', 500, 'CF_UNAVAILABLE');
  }

  return await client.unsubscribe(params);
}

export async function sendReply(
  emailId: string,
  params: {
    body: string;
    archiveAfterSend?: boolean;
  }
): Promise<{
  messageId: string;
  threadId: string;
  archived: boolean;
}> {
  if (!CF_WORKER_URL) {
    throw new EmailError('CF Worker URL not configured', 500, 'CONFIG_ERROR');
  }

  const sessionId = getStoredSessionId();
  const response = await fetch(`${CF_WORKER_URL}/api/replies/${emailId}/send`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
    },
    body: JSON.stringify(params),
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new EmailError(
      data.error?.message || 'Failed to send reply',
      response.status,
      data.error?.code
    );
  }

  return data.data;
}
